/**
 * A company's legal data (PRD 04 §2.3; ADR 0009 §7 and §8, as decided on 2026-10-18), all of it
 * optional: its legal name, which may differ from the name it shows; its tax id; its address; and
 * the address people write to about their data, better a shared mailbox than a person's. The back
 * office's wizard and «Editar» and the operator tool's `org create` take them with these rules, and
 * the table checks the same bounds (organizations, migrations 0036 and 0037).
 */
export const COMPANY_LEGAL_FIELDS = ['legalName', 'taxId', 'address', 'privacyContact'] as const
export type CompanyLegalField = (typeof COMPANY_LEGAL_FIELDS)[number]

/** In characters: the legal name and the address are one line each; the contact, an email. */
export const COMPANY_LEGAL_LIMITS = {
  legalName: { min: 2, max: 200 },
  address: 300,
  privacyContact: 254,
} as const

/** A Spanish CIF, NIF or NIE: none starts with «ES», as a CIF has a digit after its letter. */
const SPANISH_TAX_ID = /^(?:[A-HJNP-SUVW]\d{7}[0-9A-J]|\d{8}[A-Z]|[XYZ]\d{7}[A-Z])$/

/** The VAT prefixes of the EU other than Spain's, which is always «ES» and a Spanish id. */
const EU_VAT_PREFIXES =
  'AT|BE|BG|CY|CZ|DE|DK|EE|EL|FI|FR|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK|XI'

/**
 * A Spanish CIF, NIF or NIE, or an intra-community VAT number, by its format only (no check digit,
 * PRD 04 §2.3), in upper case and without spaces, dots or hyphens; null if it is none of them. A
 * Spanish VAT number is stored as the Spanish id it holds, without «ES» (decided on 2026-11-02), so
 * that the same company cannot be there twice; «ES» followed by anything else is refused. Always 4
 * to 14 letters and digits, never starting with «ES», as the table checks
 * (organizations_tax_id_check).
 */
export function normalizeTaxId(raw: string): string | null {
  const v = raw.toUpperCase().replace(/[\s.-]/g, '')
  if (v.startsWith('ES')) {
    const id = v.slice(2)
    return SPANISH_TAX_ID.test(id) ? id : null
  }
  const valid =
    SPANISH_TAX_ID.test(v) ||
    (new RegExp(`^(${EU_VAT_PREFIXES})[A-Z0-9]{2,12}$`).test(v) && /\d/.test(v))
  return valid ? v : null
}
